import { Link } from "react-router-dom";
import { ShieldCheck, Info } from "lucide-react";
import Seo from "@/components/Seo";

const STEPS = [
  {
    n: "01",
    title: "Initial conversation",
    desc: "A Maritime Thermal Solutions representative speaks with the homeowner in person or by phone and explains who we are and why we're reaching out.",
  },
  {
    n: "02",
    title: "Basic home information",
    desc: "If you're interested, we ask a few basic questions about your home and current heating setup — things like home size and how you heat today.",
  },
  {
    n: "03",
    title: "Appointment coordination",
    desc: "If you'd like to take the next step, we coordinate a no-obligation appointment time with a trusted HVAC partner that serves your area.",
  },
  {
    n: "04",
    title: "HVAC partner assessment",
    desc: "The partner HVAC company attends the appointment, completes the in-home assessment, and — if appropriate — provides any quote or recommendations.",
  },
  {
    n: "05",
    title: "Homeowner decision",
    desc: "You decide what to do next. There is no obligation to purchase anything from Maritime Thermal Solutions or the partner HVAC company.",
  },
];

const FAQS = [
  {
    q: "Is Maritime Thermal Solutions an HVAC installer?",
    a: "No. We coordinate appointments and provide homeowner education. All HVAC assessments, quotes, and installations are handled by partner HVAC companies that serve your area.",
  },
  {
    q: "Is the appointment free?",
    a: "Yes. The in-home assessment is offered on a no-obligation basis. You are not required to purchase anything as a result.",
  },
  {
    q: "Am I required to buy anything?",
    a: "No. The appointment is informational. You can decide afterwards whether you'd like to move forward, get other quotes, or do nothing at all.",
  },
  {
    q: "Who will come to my home?",
    a: "A technician or representative from one of our partner HVAC companies — not a Maritime Thermal Solutions representative. They handle the assessment and any potential quote.",
  },
  {
    q: "Can I cancel or change my appointment?",
    a: "Yes. You can cancel or reschedule at any time. Please call us at (902) 706-6287 or use the contact form, and we'll update the appointment for you.",
  },
  {
    q: "Does Maritime Thermal Solutions represent the government or a utility?",
    a: "No. We are an independent local company. We are not a government agency, not a utility, and we do not represent any rebate or government program.",
  },
  {
    q: "How do I verify a representative?",
    a: "Use our Verify a Representative form or call us directly at (902) 706-6287. We'll confirm whether the person who visited your home is associated with us.",
  },
];

export default function HowItWorks() {
  return (
    <>
      <Seo
        title="How It Works | Heat Pump Appointment Process in Nova Scotia"
        description="See how Maritime Thermal Solutions helps homeowners understand heat pump options and coordinate appointments with local HVAC partners."
      />

      <section className="border-b border-slate-200 bg-slate-50">
        <div className="container-x py-14 md:py-20">
          <p className="eyebrow">Our Process</p>
          <h1 className="mt-3 max-w-3xl font-heading text-4xl font-bold leading-tight text-brand-navy sm:text-5xl" data-testid="how-heading">
            How Our Appointment Process Works
          </h1>
          <p className="mt-5 max-w-3xl text-base leading-relaxed text-slate-600 sm:text-lg">
            A simple, transparent process — from the first conversation at your door to the
            assessment by a partner HVAC company. You stay in control at every step.
          </p>
        </div>
      </section>

      <section className="section-y" data-testid="how-steps">
        <div className="container-x">
          <ol className="space-y-4">
            {STEPS.map((step) => (
              <li
                key={step.n}
                className="rounded-lg border border-slate-200 bg-white p-6 sm:p-8"
                data-testid={`how-step-${step.n}`}
              >
                <div className="grid gap-4 sm:grid-cols-12 sm:gap-6">
                  <div className="sm:col-span-2">
                    <div className="font-heading text-4xl font-bold text-brand-orange">{step.n}</div>
                  </div>
                  <div className="sm:col-span-10">
                    <h3 className="font-heading text-xl font-semibold text-slate-900">{step.title}</h3>
                    <p className="mt-2 text-base leading-relaxed text-slate-600">{step.desc}</p>
                  </div>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-10 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-5" data-testid="how-disclaimer">
            <Info size={18} className="mt-0.5 shrink-0 text-amber-700" aria-hidden="true" />
            <p className="text-sm leading-relaxed text-amber-900">
              <strong>Important:</strong> Maritime Thermal Solutions does not provide quotes,
              install equipment, or make final system recommendations. Those services are
              provided by the partner HVAC company attending the appointment.
            </p>
          </div>
        </div>
      </section>

      <section className="section-y bg-slate-50" data-testid="how-faq">
        <div className="container-x">
          <div className="max-w-2xl">
            <p className="eyebrow">FAQ</p>
            <h2 className="mt-2 font-heading text-3xl font-semibold text-slate-900 sm:text-4xl">
              Frequently asked questions
            </h2>
          </div>
          <dl className="mt-10 grid gap-5 md:grid-cols-2">
            {FAQS.map((f, i) => (
              <div
                key={f.q}
                className="rounded-lg border border-slate-200 bg-white p-6"
                data-testid={`faq-item-${i + 1}`}
              >
                <dt className="font-heading text-lg font-semibold text-slate-900">{f.q}</dt>
                <dd className="mt-2 text-sm leading-relaxed text-slate-600">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="section-y" data-testid="how-cta">
        <div className="container-x rounded-lg border border-slate-200 bg-white p-8 sm:p-12">
          <div className="grid items-center gap-6 md:grid-cols-2">
            <div>
              <h2 className="font-heading text-2xl font-semibold text-slate-900 sm:text-3xl">
                Questions about a visit or appointment?
              </h2>
              <p className="mt-3 text-slate-600">
                Verify a representative, confirm an appointment, or just ask us a question — we're happy to help.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <Link to="/verify" className="btn-primary" data-testid="how-verify-cta">
                <ShieldCheck size={18} aria-hidden="true" />
                Verify a Representative
              </Link>
              <Link to="/contact" className="btn-outline" data-testid="how-contact-cta">
                Contact us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
